import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Eye, Lightning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin card for triggering the inactivity ELO decay manually.
 *
 * Decay runs automatically every night at 02:00 UTC (05:00 EAT). This card
 * exists as a manual override — you can preview exactly who would be
 * affected right now and apply it ad-hoc between cron runs.
 *
 * Policy (agreed 2026-04-24):
 *   • 60-day grace period
 *   • -10 ELO per 30 days past the grace
 *   • Hard floor: 1000 (nobody drops below challenger entry)
 *   • Applies to every internal player (Premier + Challenger)
 *   • Idempotent — running this twice a day is safe
 */
export default function InactivityDecayCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/inactivity-decay/preview`, { withCredentials: true });
      setPreview(r.data);
      toast.success(
        r.data.players_with_change === 0
          ? "No decay needed — everyone active or already decayed correctly"
          : `${r.data.players_with_change} player${r.data.players_with_change === 1 ? "" : "s"} would be updated`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      "Apply inactivity ELO decay now?\n\n" +
      "• Every eligible player's ELO rating will be updated\n" +
      "• Returning players get their prior decay reversed automatically\n" +
      "• The nightly cron will keep this in sync going forward\n\n" +
      "This is idempotent and safe to run repeatedly."
    )) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/inactivity-decay/apply`, {}, { withCredentials: true });
      toast.success(r.data.message);
      // refresh the preview so admin sees the post-apply state
      await runPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const changedPlans = preview?.plans?.filter((p) => p.has_change) || [];

  return (
    <Card className="bg-card border-chart-3/30" data-testid="inactivity-decay-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Clock className="w-5 h-5 text-chart-3" weight="fill" />
          Inactivity ELO Decay
        </CardTitle>
        <CardDescription className="text-xs mt-1 leading-relaxed">
          60-day grace, then −10 ELO per 30 days, hard floor at 1000. Runs
          automatically every night at 05:00 EAT. Use this card to preview or
          trigger ad-hoc between cron runs. Returning players get their prior
          decay reversed on the next run.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline" onClick={runPreview} disabled={loading}
            className="border-secondary/40 text-secondary hover:bg-secondary/10"
            data-testid="inactivity-preview-btn"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {loading ? "Computing…" : "Preview"}
          </Button>
          <Button
            size="sm" onClick={runApply} disabled={applying || !preview}
            className="bg-chart-3 text-background hover:bg-chart-3/90"
            data-testid="inactivity-apply-btn"
          >
            <Lightning className="w-4 h-4 mr-1.5" weight="fill" />
            {applying ? "Applying…" : "Apply now"}
          </Button>
        </div>

        {preview && (
          <div className="border border-border rounded-lg" data-testid="inactivity-preview-results">
            <div className="px-4 py-2 border-b border-border bg-muted/10 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {changedPlans.length} of {preview.total_players} players affected
              </span>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                grace {preview.grace_days}d · {preview.monthly_decay}/mo · floor {preview.hard_floor}
              </span>
            </div>
            {changedPlans.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground italic">
                No changes required — everyone is active or already at the correct decay level.
              </p>
            ) : (
              <div className="divide-y divide-border max-h-80 overflow-y-auto">
                {changedPlans.map((p) => {
                  const isRestore = p.delta > 0;
                  return (
                    <div key={p.player_id} className="flex items-center gap-3 px-4 py-1.5 text-xs">
                      <Badge
                        variant="outline"
                        className={`text-[10px] capitalize w-20 justify-center ${p.tier === "premier" ? "border-primary/30 text-primary" : "border-border"}`}
                      >
                        {p.tier}
                      </Badge>
                      <span className="flex-1 font-medium truncate">{p.name}</span>
                      <span className="text-[11px] text-muted-foreground tabular-nums w-24 text-right">
                        {p.days_inactive}d idle
                      </span>
                      <span className="tabular-nums w-32 text-right">
                        {p.elo_before} →{" "}
                        <span className={`font-semibold ${isRestore ? "text-primary" : "text-destructive"}`}>
                          {p.elo_after}
                        </span>
                      </span>
                      <span className={`tabular-nums font-semibold w-16 text-right ${isRestore ? "text-primary" : "text-destructive"}`}>
                        {isRestore ? "+" : ""}{p.delta}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
